import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { 
  Organization, 
  OrganizationType,
  TenderType, 
  TenderStatus, 
  ApplicationStatus, 
  Tender, 
  Application,
  TenderDocument,
  CurrencyRate,
  Product,
  ProductCategory,
  Plan,
  BusinessLine,
  Currency,
  Offer
} from '../types';
import { useTenant } from './TenantContext';

interface DataContextType {
  // Organizations
  organizations: Organization[];
  organizationTypes: OrganizationType[];
  addOrganization: (org: Omit<Organization, 'id' | 'company_id' | 'created_at'>) => Promise<Organization | null>;
  updateOrganization: (id: string, org: Partial<Organization>) => Promise<Organization | null>;
  deleteOrganization: (id: string) => void;

  // Tender Types
  tenderTypes: TenderType[];
  addTenderType: (type: Omit<TenderType, 'id' | 'company_id' | 'created_at'>) => void;
  updateTenderType: (id: string, type: Partial<TenderType>) => void;
  deleteTenderType: (id: string) => void;

  // Tender Status
  tenderStatuses: TenderStatus[];
  addTenderStatus: (status: Omit<TenderStatus, 'id' | 'company_id' | 'created_at'>) => void;
  updateTenderStatus: (id: string, status: Partial<TenderStatus>) => void;
  deleteTenderStatus: (id: string) => void;

  // Application Status
  applicationStatuses: ApplicationStatus[];
  addApplicationStatus: (status: Omit<ApplicationStatus, 'id' | 'company_id' | 'created_at'>) => void;
  updateApplicationStatus: (id: string, status: Partial<ApplicationStatus>) => void;
  deleteApplicationStatus: (id: string) => void;

  // Tenders
  tenders: Tender[];
  addTender: (tender: Omit<Tender, 'id' | 'company_id' | 'created_at'>) => Promise<Tender | null>;
  updateTender: (id: string, tender: Partial<Tender>) => Promise<Tender | null>;
  deleteTender: (id: string) => void;

  // Tender Documents
  tenderDocuments: TenderDocument[];
  addTenderDocument: (doc: Omit<TenderDocument, 'id' | 'company_id' | 'created_at'>) => void;
  updateTenderDocument: (id: string, doc: Partial<TenderDocument>) => void;
  deleteTenderDocument: (id: string) => void;

  // Applications
  applications: Application[];
  addApplication: (app: Omit<Application, 'id' | 'company_id' | 'created_at'>) => void;
  updateApplication: (id: string, app: Partial<Application>) => void;
  deleteApplication: (id: string) => void;

  // Products
  products: Product[];
  addProduct: (product: Omit<Product, 'id' | 'company_id' | 'created_at'>) => void;
  updateProduct: (id: string, product: Partial<Product>) => void;
  deleteProduct: (id: string) => void;

  // Product Categories
  productCategories: ProductCategory[];
  addProductCategory: (category: Omit<ProductCategory, 'id' | 'company_id' | 'created_at'>) => Promise<ProductCategory | null>;
  updateProductCategory: (id: string, category: Partial<ProductCategory>) => Promise<ProductCategory | null>;
  deleteProductCategory: (id: string) => void;

  // Currency Rates
  currencyRates: CurrencyRate[];
  addCurrencyRate: (rate: Omit<CurrencyRate, 'id' | 'company_id' | 'created_at'>) => void;
  updateCurrencyRate: (id: string, rate: Partial<CurrencyRate>) => void;
  deleteCurrencyRate: (id: string) => void;

  // Currencies
  currencies: Currency[];
  loadCurrencies: () => Promise<void>;
  addCurrency: (currency: Omit<Currency, 'id' | 'created_at'>) => void;
  updateCurrency: (id: string, currency: Partial<Currency>) => void;
  deleteCurrency: (id: string) => void;

  // Plans
  plans: Plan[];
  loadPlans: () => Promise<void>;
  addPlan: (plan: Omit<Plan, 'id' | 'created_at'>) => void;
  updatePlan: (id: string, plan: Partial<Plan>) => void;
  deletePlan: (id: string) => void;

  // Business Lines
  businessLines: BusinessLine[];
  addBusinessLine: (line: Omit<BusinessLine, 'id' | 'company_id' | 'created_at'>) => Promise<boolean>;
  updateBusinessLine: (id: string, line: Partial<BusinessLine>) => void;
  deleteBusinessLine: (id: string) => void;

  // Offers
  offers: Offer[];
  addOffer: (offer: Omit<Offer, 'id' | 'company_id' | 'created_at' | 'winner' | 'status'>) => Promise<Offer | null>;
  updateOffer: (id: string, offer: Partial<Offer>) => Promise<Offer | null>;
  deleteOffer: (id: string) => Promise<void>;
  getOffersByApplication: (applicationId: string) => Offer[];
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentCompany, isLoading: tenantLoading } = useTenant();
  
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationTypes, setOrganizationTypes] = useState<OrganizationType[]>([]);
  const [tenderTypes, setTenderTypes] = useState<TenderType[]>([]);
  const [tenderStatuses, setTenderStatuses] = useState<TenderStatus[]>([]);
  const [applicationStatuses, setApplicationStatuses] = useState<ApplicationStatus[]>([]);
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [tenderDocuments, setTenderDocuments] = useState<TenderDocument[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
  
  // Debug: Log cuando cambian las categorías
  useEffect(() => {
    console.log('Estado productCategories actualizado:', productCategories.length, 'categorías');
  }, [productCategories]);
  const [currencyRates, setCurrencyRates] = useState<CurrencyRate[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [businessLines, setBusinessLines] = useState<BusinessLine[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  
  // Usar useRef para rastrear el ID de la empresa actual y evitar recargas innecesarias
  const currentCompanyIdRef = useRef<string | null>(null);
  const hasLoadedDataRef = useRef<boolean>(false);
  const isLoadingRef = useRef<boolean>(false);

  useEffect(() => {
    // Si está cargando, no hacer nada (pero no resetear referencias)
    if (tenantLoading) {
      // Si ya hay datos cargados, no resetear las referencias cuando tenantLoading cambia
      // Esto evita recargas cuando loadCompanies() se ejecuta pero la empresa no cambia
      return;
    }
    
    // Si no hay empresa, resetear las referencias
    if (!currentCompany) {
      currentCompanyIdRef.current = null;
      hasLoadedDataRef.current = false;
      isLoadingRef.current = false;
      return;
    }
    
    // Solo recargar datos si:
    // 1. El ID de la empresa realmente cambió, O
    // 2. Es la primera vez que se carga (hasLoadedDataRef.current === false)
    const companyIdChanged = currentCompanyIdRef.current !== currentCompany.id;
    
    // Si ya está cargando, no iniciar otra carga
    if (isLoadingRef.current) {
      console.log('Ya se está cargando, evitando carga duplicada');
      return;
    }
    
    if (!companyIdChanged && hasLoadedDataRef.current) {
      // Misma empresa y ya se cargaron los datos - no recargar
      console.log('Misma empresa, no se recargan datos. ID:', currentCompany.id);
      return;
    }
    
    // Empresa cambió o es la primera carga
    if (companyIdChanged) {
      console.log('Empresa cambió, recargando datos. ID anterior:', currentCompanyIdRef.current, 'ID nuevo:', currentCompany.id);
    } else {
      console.log('Primera carga de datos para la empresa:', currentCompany.id);
    }
    
    // Marcar que estamos cargando ANTES de actualizar referencias
    // Esto previene cargas duplicadas si el useEffect se ejecuta de nuevo
    isLoadingRef.current = true;
    
    // Actualizar referencias ANTES de cargar para evitar que se ejecute de nuevo
    currentCompanyIdRef.current = currentCompany.id;
    hasLoadedDataRef.current = true;
    
    // Cargar datos de forma asíncrona
    loadAllData()
      .then(() => {
        console.log('Carga de datos completada exitosamente');
      })
      .catch((error) => {
        console.error('Error en carga de datos:', error);
        // Si hay error, resetear el flag para permitir reintento
        hasLoadedDataRef.current = false;
      })
      .finally(() => {
        isLoadingRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id, tenantLoading]);

  const loadAllData = async () => {
    if (!currentCompany) return;

    try {
      console.log('Loading data from Supabase for company:', currentCompany.id);

      // Load plans (global)
      await loadPlans();
      
      // Load currencies (global)
      await loadCurrencies();

      // Load organization types (global)
      const { data: orgTypes, error: orgTypesError } = await supabase
        .from('organization_types')
        .select('*')
        .order('name');

      if (orgTypesError) {
        console.error('Error loading organization types:', orgTypesError);
      } else {
        setOrganizationTypes(orgTypes || []);
      }

      // Load company-specific data
      const companyId = currentCompany.id;

      // Load organizations
      const { data: orgs, error: orgsError } = await supabase
        .from('organizations')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (orgsError) {
        console.error('Error loading organizations:', orgsError);
      } else {
        setOrganizations(orgs || []);
      }

      // Load tender types
      const { data: tTypes, error: tTypesError } = await supabase
        .from('tender_types')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (tTypesError) {
        console.error('Error loading tender types:', tTypesError);
      } else {
        setTenderTypes(tTypes || []);
      }

      // Load tender statuses
      const { data: tStatuses, error: tStatusesError } = await supabase
        .from('tender_statuses')
        .select('*')
        .order('created_at', { ascending: false });

      if (tStatusesError) {
        console.error('Error loading tender statuses:', tStatusesError);
      } else {
        setTenderStatuses(tStatuses || []);
      }

      // Load application statuses
      const { data: appStatuses, error: appStatusesError } = await supabase
        .from('application_statuses')
        .select('*')
        .order('order_index');

      if (appStatusesError) {
        console.error('Error loading application statuses:', appStatusesError);
      } else {
        setApplicationStatuses(appStatuses || []);
      }

      // Load tenders
      // Load tenders with pagination to handle large datasets
      await loadTenders(companyId);


      // Load applications
      const { data: appsData, error: appsError } = await supabase
        .from('applications')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (appsError) {
        console.error('Error loading applications:', appsError);
      } else {
        setApplications(appsData || []);
      }

      // Load product categories
      console.log('Cargando categorías para empresa:', companyId);
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('product_categories')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (categoriesError) {
        console.error('Error loading product categories:', categoriesError);
      } else {
        console.log('Categorías cargadas desde DB:', categoriesData);
        setProductCategories(categoriesData || []);
      }

      // Load products
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (productsError) {
        console.error('Error loading products:', productsError);
      } else {
        const productsWithCreatedAt = productsData?.map(product => ({
          ...product,
          createdAt: product.created_at // Add alias for compatibility
        })) || [];
        setProducts(productsWithCreatedAt);
      }

      // Load currency rates
      const { data: ratesData, error: ratesError } = await supabase
        .from('currency_rates')
        .select('*')
        .eq('company_id', companyId)
        .order('date', { ascending: false });

      if (ratesError) {
        console.error('Error loading currency rates:', ratesError);
      } else {
        setCurrencyRates(ratesData || []);
      }

      // Load tender documents
      const { data: docsData, error: docsError } = await supabase
        .from('tender_documents')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (docsError) {
        console.error('Error loading tender documents:', docsError);
      } else {
        setTenderDocuments(docsData || []);
      }

      // Load business lines
      const { data: businessLinesData, error: businessLinesError } = await supabase
        .from('business_lines')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (businessLinesError) {
        console.error('Error loading business lines:', businessLinesError);
      } else {
        setBusinessLines(businessLinesData || []);
      }

      // Load offers
      const { data: offersData, error: offersError } = await supabase
        .from('offers')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (offersError) {
        console.error('Error loading offers:', offersError);
      } else {
        setOffers(offersData || []);
      }

      console.log('Data loaded successfully from Supabase');
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  // Function to load tenders with pagination
  const loadTenders = async (companyId: string) => {
    try {
      console.log('Loading tenders for company:', companyId);
      
      // First, get the count of all tenders
      const { count, error: countError } = await supabase
        .from('tenders')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', companyId);
      
      if (countError) {
        console.error('Error counting tenders:', countError);
        return;
      }
      
      console.log(`Found ${count} tenders total`);
      
      // If there are no tenders, return early
      if (!count || count === 0) {
        setTenders([]);
        return;
      }
      
      // Calculate how many pages we need to fetch (1000 per page)
      const pageSize = 1000;
      const pages = Math.ceil(count / pageSize);
      
      // Fetch all pages
      let allTenders: Tender[] = [];
      
      for (let page = 0; page < pages; page++) {
        const from = page * pageSize;
        const to = from + pageSize - 1;
        
        console.log(`Fetching tenders page ${page + 1}/${pages} (${from}-${to})`);
        
        const { data, error } = await supabase
          .from('tenders')
          .select('*')
          .eq('company_id', companyId)
          .order('created_at', { ascending: false })
          .range(from, to);
        
        if (error) {
          console.error(`Error loading tenders page ${page + 1}:`, error);
          continue;
        }
        
        if (data) {
          allTenders = [...allTenders, ...data];
          console.log(`Loaded ${data.length} tenders from page ${page + 1}`);
        }
      }
      
      console.log(`Loaded ${allTenders.length} tenders total`);
      setTenders(allTenders);
    } catch (error) {
      console.error('Error loading tenders:', error);
    }
  };

  // CRUD functions for organizations
  const addOrganization = async (orgData: Omit<Organization, 'id' | 'company_id' | 'created_at'>): Promise<Organization | null> => {
    if (!currentCompany) return null;

    try {
      const { data, error } = await supabase
      .from('organizations')
      .insert([{
        ...orgData,
        company_id: currentCompany.id
      }])
      .select()
        .single();

      if (error) {
        console.error('Error adding organization:', error);
        return null;
      }

      if (data) {
        setOrganizations(prev => [...prev, data as Organization]);
        return data as Organization;
      }

      return null;
    } catch (error) {
      console.error('Error adding organization:', error);
      return null;
    }
  };

  const updateOrganization = async (id: string, orgData: Partial<Organization>): Promise<Organization | null> => {
    try {
      const { data, error } = await supabase
      .from('organizations')
      .update(orgData)
      .eq('id', id)
      .select()
        .single();

      if (error) {
        console.error('Error updating organization:', error);
        return null;
      }

      if (data) {
        setOrganizations(prev => prev.map(org => 
          org.id === id ? data as Organization : org
        ));
        return data as Organization;
      }

      return null;
    } catch (error) {
      console.error('Error updating organization:', error);
      return null;
    }
  };

  const deleteOrganization = (id: string) => {
    supabase
      .from('organizations')
      .delete()
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.error('Error deleting organization:', error);
        } else {
          setOrganizations(prev => prev.filter(org => org.id !== id));
        }
      });
  };

  // CRUD functions for tender types
  const addTenderType = (typeData: Omit<TenderType, 'id' | 'company_id' | 'created_at'>) => {
    if (!currentCompany) return;

    supabase
      .from('tender_types')
      .insert([{
        ...typeData,
        company_id: currentCompany.id
      }])
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error adding tender type:', error);
        } else if (data) {
          setTenderTypes(prev => [...prev, data as TenderType]);
        }
      });
  };

  const updateTenderType = (id: string, typeData: Partial<TenderType>) => {
    supabase
      .from('tender_types')
      .update(typeData)
      .eq('id', id)
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error updating tender type:', error);
        } else if (data) {
          setTenderTypes(prev => prev.map(type => 
            type.id === id ? data as TenderType : type
          ));
        }
      });
  };

  const deleteTenderType = (id: string) => {
    supabase
      .from('tender_types')
      .delete()
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.error('Error deleting tender type:', error);
        } else {
          setTenderTypes(prev => prev.filter(type => type.id !== id));
        }
      });
  };

  // CRUD functions for tender statuses
  const addTenderStatus = (statusData: Omit<TenderStatus, 'id' | 'company_id' | 'created_at'>) => {
    // Solo Licitatrak puede agregar estados globales
    if (!currentCompany || currentCompany.rut !== '99.999.999-9') {
      console.error('Solo Licitatrak puede gestionar estados de licitación globales');
      return;
    }

    supabase
      .from('tender_statuses')
      .insert([{
        ...statusData,
        company_id: currentCompany.id // Mantener referencia a Licitatrak
      }])
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error adding tender status:', error);
        } else if (data) {
          setTenderStatuses(prev => [...prev, data as TenderStatus]);
        }
      });
  };

  const updateTenderStatus = (id: string, statusData: Partial<TenderStatus>) => {
    // Solo Licitatrak puede actualizar estados globales
    if (!currentCompany || currentCompany.rut !== '99.999.999-9') {
      console.error('Solo Licitatrak puede gestionar estados de licitación globales');
      return;
    }

    supabase
      .from('tender_statuses')
      .update(statusData)
      .eq('id', id)
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error updating tender status:', error);
        } else if (data) {
          setTenderStatuses(prev => prev.map(status => 
            status.id === id ? data as TenderStatus : status
          ));
        }
      });
  };

  const deleteTenderStatus = (id: string) => {
    // Solo Licitatrak puede eliminar estados globales
    if (!currentCompany || currentCompany.rut !== '99.999.999-9') {
      console.error('Solo Licitatrak puede gestionar estados de licitación globales');
      return;
    }

    supabase
      .from('tender_statuses')
      .delete()
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.error('Error deleting tender status:', error);
        } else {
          setTenderStatuses(prev => prev.filter(status => status.id !== id));
        }
      });
  };

  // CRUD functions for application statuses
  const addApplicationStatus = (statusData: Omit<ApplicationStatus, 'id' | 'company_id' | 'created_at'>) => {
    // Solo Licitatrak puede agregar estados globales
    if (!currentCompany || currentCompany.rut !== '99.999.999-9') {
      console.error('Solo Licitatrak puede gestionar estados de postulación globales');
      return;
    }

    supabase
      .from('application_statuses')
      .insert([{
        ...statusData,
        company_id: currentCompany.id // Mantener referencia a Licitatrak
      }])
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error adding application status:', error);
        } else if (data) {
          setApplicationStatuses(prev => [...prev, data as ApplicationStatus]);
        }
      });
  };

  const updateApplicationStatus = (id: string, statusData: Partial<ApplicationStatus>) => {
    // Solo Licitatrak puede actualizar estados globales
    if (!currentCompany || currentCompany.rut !== '99.999.999-9') {
      console.error('Solo Licitatrak puede gestionar estados de postulación globales');
      return;
    }

    supabase
      .from('application_statuses')
      .update(statusData)
      .eq('id', id)
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error updating application status:', error);
        } else if (data) {
          setApplicationStatuses(prev => prev.map(status => 
            status.id === id ? data as ApplicationStatus : status
          ));
        }
      });
  };

  const deleteApplicationStatus = (id: string) => {
    // Solo Licitatrak puede eliminar estados globales
    if (!currentCompany || currentCompany.rut !== '99.999.999-9') {
      console.error('Solo Licitatrak puede gestionar estados de postulación globales');
      return;
    }

    supabase
      .from('application_statuses')
      .delete()
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.error('Error deleting application status:', error);
        } else {
          setApplicationStatuses(prev => prev.filter(status => status.id !== id));
        }
      });
  };

  // CRUD functions for tenders
  const addTender = async (tenderData: Omit<Tender, 'id' | 'company_id' | 'created_at'>): Promise<Tender | null> => {
    if (!currentCompany) return null;

    try {
      const { data, error } = await supabase
      .from('tenders')
      .insert([{
        ...tenderData,
        company_id: currentCompany.id
      }])
      .select()
        .single();

      if (error) {
        console.error('Error adding tender:', error);
        return null;
      }

      if (data) {
        setTenders(prev => [...prev, data as Tender]);
        return data as Tender;
      }

      return null;
    } catch (error) {
      console.error('Error adding tender:', error);
      return null;
    }
  };

  const updateTender = async (id: string, tenderData: Partial<Tender>): Promise<Tender | null> => {
    try {
      const { data, error } = await supabase
      .from('tenders')
      .update(tenderData)
      .eq('id', id)
      .select()
        .single();

      if (error) {
        console.error('Error updating tender:', error);
        return null;
      }

      if (data) {
        setTenders(prev => prev.map(tender => 
          tender.id === id ? data as Tender : tender
        ));
        return data as Tender;
      }

      return null;
    } catch (error) {
      console.error('Error updating tender:', error);
      return null;
    }
  };

  const deleteTender = (id: string) => {
    // Verificar si la licitación tiene postulaciones
    const hasApplications = applications.some(app => app.tender_id === id);
    
    if (hasApplications) {
      console.error('No se puede eliminar una licitación que tiene postulaciones asociadas');
      return;
    }
    
    supabase
      .from('tenders')
      .delete()
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.error('Error deleting tender:', error);
        } else {
          setTenders(prev => prev.filter(tender => tender.id !== id));
        }
      });
  };

  // CRUD functions for tender documents
  const addTenderDocument = (docData: Omit<TenderDocument, 'id' | 'company_id' | 'created_at'>) => {
    if (!currentCompany) return;

    supabase
      .from('tender_documents')
      .insert([{
        ...docData,
        company_id: currentCompany.id
      }])
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error adding tender document:', error);
        } else if (data) {
          setTenderDocuments(prev => [...prev, data as TenderDocument]);
        }
      });
  };

  const updateTenderDocument = (id: string, docData: Partial<TenderDocument>) => {
    supabase
      .from('tender_documents')
      .update(docData)
      .eq('id', id)
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error updating tender document:', error);
        } else if (data) {
          
          setTenderDocuments(prev => prev.map(doc => 
            doc.id === id ? data as TenderDocument : doc
          ));
        }
      });
  };

  const deleteTenderDocument = (id: string) => {
    supabase
      .from('tender_documents')
      .delete()
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.error('Error deleting tender document:', error);
        } else {
          setTenderDocuments(prev => prev.filter(doc => doc.id !== id));
        }
      });
  };

  // CRUD functions for applications
  const addApplication = (appData: Omit<Application, 'id' | 'company_id' | 'created_at'>) => {
    if (!currentCompany) return;

    const applicationWithDefaults = {
      ...appData,
      company_id: currentCompany.id
    };

    supabase
      .from('applications')
      .insert([applicationWithDefaults])
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error adding application:', error);
        } else if (data) {
          // Actualizar también el tender relacionado con el nuevo estado
          if (data.status_id) {
            setTenders(prev => prev.map(tender => 
              tender.id === data.tender_id 
                ? { ...tender, application_status_id: data.status_id }
                : tender
            ));
          }
          
          setApplications(prev => [...prev, data as Application]);
        }
      });
  };

  const updateApplication = (id: string, appData: Partial<Application>) => {
    const oldApplication = applications.find(app => app.id === id);
    
    supabase
      .from('applications')
      .update(appData)
      .eq('id', id)
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error updating application:', error);
        } else if (data) {
          // Si cambió el status_id, también actualizar el tender relacionado
          if (oldApplication && appData.status_id && appData.status_id !== oldApplication.status_id) {
            // El trigger de la base de datos ya maneja la sincronización
            // pero también actualizamos localmente para reflejar el cambio inmediatamente
            setTenders(prev => prev.map(tender => 
              tender.id === data.tender_id 
                ? { ...tender, application_status_id: appData.status_id }
                : tender
            ));
          }
          
          setApplications(prev => prev.map(app => 
            app.id === id ? data as Application : app
          ));
        }
      });
  };

  const deleteApplication = (id: string) => {
    supabase
      .from('applications')
      .delete()
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.error('Error deleting application:', error);
        } else {
          setApplications(prev => prev.filter(app => app.id !== id));
        }
      });
  };

  // CRUD functions for products
  const addProduct = (productData: Omit<Product, 'id' | 'company_id' | 'created_at'>) => {
    if (!currentCompany) return;

    // Ensure createdAt is not included in the data sent to Supabase
    const { createdAt, ...productDataToSend } = productData as any;

    supabase
      .from('products')
      .insert([{
        ...productDataToSend,
        company_id: currentCompany.id
      }])
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error adding product:', error);
        } else if (data) {
          const productWithCreatedAt = {
            ...data,
            createdAt: data.created_at // Add alias for compatibility
          };
          setProducts(prev => [...prev, productWithCreatedAt as Product]);
        }
      });
  };

  const updateProduct = (id: string, productData: Partial<Product>) => {
    // Ensure createdAt is not included in the data sent to Supabase
    const { createdAt, ...productDataToSend } = productData as any;

    supabase
      .from('products')
      .update(productDataToSend)
      .eq('id', id)
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error updating product:', error);
        } else if (data) {
          const productWithCreatedAt = {
            ...data,
            createdAt: data.created_at // Add alias for compatibility
          };
          setProducts(prev => prev.map(product => 
            product.id === id ? productWithCreatedAt as Product : product
          ));
        }
      });
  };

  const deleteProduct = (id: string) => {
    supabase
      .from('products')
      .delete()
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.error('Error deleting product:', error);
        } else {
          setProducts(prev => prev.filter(product => product.id !== id));
        }
      });
  };

  // CRUD functions for product categories
  const addProductCategory = async (categoryData: Omit<ProductCategory, 'id' | 'company_id' | 'created_at'>): Promise<ProductCategory | null> => {
    if (!currentCompany) return null;

    try {
      const { data, error } = await supabase
        .from('product_categories')
        .insert([{
          ...categoryData,
          company_id: currentCompany.id
        }])
        .select()
        .single();

      if (error) {
        console.error('Error adding product category:', error);
        return null;
      }

      if (data) {
        setProductCategories(prev => [...prev, data]);
        return data;
      }
    } catch (error) {
      console.error('Error adding product category:', error);
    }
    return null;
  };

  const updateProductCategory = async (id: string, categoryData: Partial<ProductCategory>): Promise<ProductCategory | null> => {
    try {
      const { data, error } = await supabase
        .from('product_categories')
        .update(categoryData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating product category:', error);
        return null;
      }

      if (data) {
        setProductCategories(prev => prev.map(category => 
          category.id === id ? data : category
        ));
        return data;
      }
    } catch (error) {
      console.error('Error updating product category:', error);
    }
    return null;
  };

  const deleteProductCategory = (id: string) => {
    supabase
      .from('product_categories')
      .delete()
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.error('Error deleting product category:', error);
        } else {
          setProductCategories(prev => prev.filter(category => category.id !== id));
        }
      });
  };

  // CRUD functions for currency rates
  const addCurrencyRate = (rateData: Omit<CurrencyRate, 'id' | 'company_id' | 'created_at'>) => {
    if (!currentCompany) return;

    supabase
      .from('currency_rates')
      .insert([{
        ...rateData,
        company_id: currentCompany.id
      }])
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error adding currency rate:', error);
        } else if (data) {
          setCurrencyRates(prev => [...prev, data as CurrencyRate]);
        }
      });
  };

  const updateCurrencyRate = (id: string, rateData: Partial<CurrencyRate>) => {
    supabase
      .from('currency_rates')
      .update(rateData)
      .eq('id', id)
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error updating currency rate:', error);
        } else if (data) {
          setCurrencyRates(prev => prev.map(rate => 
            rate.id === id ? data as CurrencyRate : rate
          ));
        }
      });
  };

  const deleteCurrencyRate = (id: string) => {
    supabase
      .from('currency_rates')
      .delete()
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.error('Error deleting currency rate:', error);
        } else {
          setCurrencyRates(prev => prev.filter(rate => rate.id !== id));
        }
      });
  };

  // CRUD functions for currencies
  const loadCurrencies = async () => {
    try {
      console.log('Loading currencies...');
      const { data, error } = await supabase
        .from('currencies')
        .select('*')
        .order('code');

      if (error) {
        console.error('Error loading currencies:', error);
      } else {
        console.log('Currencies loaded successfully:', data?.length || 0);
        setCurrencies(data || []);
      }
    } catch (error) {
      console.error('Error loading currencies:', error);
    }
  };

  const addCurrency = (currencyData: Omit<Currency, 'id' | 'created_at'>) => {
    // Solo Licitatrak puede agregar divisas
    if (!currentCompany || currentCompany.rut !== '99.999.999-9') {
      console.error('Solo Licitatrak puede gestionar divisas');
      return;
    }

    supabase
      .from('currencies')
      .insert([currencyData])
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error adding currency:', error);
        } else if (data) {
          setCurrencies(prev => [...prev, data as Currency]);
        }
      });
  };

  const updateCurrency = (id: string, currencyData: Partial<Currency>) => {
    // Solo Licitatrak puede actualizar divisas
    if (!currentCompany || currentCompany.rut !== '99.999.999-9') {
      console.error('Solo Licitatrak puede gestionar divisas');
      return;
    }

    supabase
      .from('currencies')
      .update(currencyData)
      .eq('id', id)
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error updating currency:', error);
        } else if (data) {
          setCurrencies(prev => prev.map(currency => 
            currency.id === id ? data as Currency : currency
          ));
        }
      });
  };

  const deleteCurrency = (id: string) => {
    // Solo Licitatrak puede eliminar divisas
    if (!currentCompany || currentCompany.rut !== '99.999.999-9') {
      console.error('Solo Licitatrak puede gestionar divisas');
      return;
    }

    supabase
      .from('currencies')
      .delete()
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.error('Error deleting currency:', error);
        } else {
          setCurrencies(prev => prev.filter(currency => currency.id !== id));
        }
      });
  };

  // Plans functions
  const loadPlans = async () => {
    try {
      console.log('Loading plans...');
      const { data, error } = await supabase
        .from('plans')
        .select('*')
        .order('value');

      if (error) {
        console.error('Error loading plans:', error);
      } else {
        console.log('Plans loaded successfully:', data?.length || 0);
        setPlans(data || []);
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const addPlan = (planData: Omit<Plan, 'id' | 'created_at'>) => {
    supabase
      .from('plans')
      .insert([planData])
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error adding plan:', error);
        } else if (data) {
          setPlans(prev => [...prev, data as Plan]);
        }
      });
  };

  const updatePlan = (id: string, planData: Partial<Plan>) => {
    supabase
      .from('plans')
      .update(planData)
      .eq('id', id)
      .select()
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error('Error updating plan:', error);
        } else if (data) {
          setPlans(prev => prev.map(plan => 
            plan.id === id ? data as Plan : plan
          ));
        }
      });
  };

  const deletePlan = (id: string) => {
    supabase
      .from('plans')
      .delete()
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.error('Error deleting plan:', error);
        } else {
          setPlans(prev => prev.filter(plan => plan.id !== id));
        }
      });
  };

  // Business Lines functions
  const addBusinessLine = async (lineData: Omit<BusinessLine, 'id' | 'company_id' | 'created_at'>): Promise<boolean> => {
    if (!currentCompany) {
      console.error('No current company found');
      return false;
    }

    // Verificar si la empresa ha aceptado los términos
    if (!currentCompany.accept) {
      alert('Debe aceptar los términos y condiciones antes de crear líneas de negocio. Vaya a "Políticas de Privacidad" en el menú para aceptar los términos.');
      return false;
    }

    // Obtener límites desde la base de datos
    const maxBusinessLines = currentCompany.business_line || 10;
    const maxWords = currentCompany.business_word || 10;
    
    if (businessLines.length >= maxBusinessLines) {
      alert(`Has alcanzado el límite de ${maxBusinessLines} líneas de negocio.`);
      return false;
    }
    
    if (lineData.keywords.length > maxWords) {
      alert(`No se pueden agregar más de ${maxWords} palabras clave por línea de negocio.`);
      return false;
    }

    try {
      console.log('Adding business line:', lineData);
      
      const { data, error } = await supabase
        .from('business_lines')
        .insert([{
          ...lineData,
          company_id: currentCompany.id
        }])
        .select()
        .single();

      if (error) {
        console.error('Error adding business line:', error);
        
        // Show user-friendly error message
        if (error.message.includes('timeout') || error.message.includes('Request timeout')) {
          alert('Error de conexión: La operación tardó demasiado tiempo. Por favor, verifica tu conexión a internet e intenta nuevamente.');
        } else if (error.message.includes('JWT')) {
          alert('Error de autenticación: Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
        } else if (error.message.includes('permission')) {
          alert('Error de permisos: No tienes permisos para realizar esta acción.');
        } else {
          alert(`Error al crear la línea de negocio: ${error.message}`);
        }
        return false; // Return false to indicate failure
      }

      if (data) {
        setBusinessLines(prev => [...prev, data as BusinessLine]);
        console.log('Business line added successfully:', data);
        return true; // Return true to indicate success
      }
      
      return false;
    } catch (error) {
      console.error('Unexpected error adding business line:', error);
      alert('Error inesperado al crear la línea de negocio. Por favor, intenta nuevamente.');
      return false;
    }
  };

  const updateBusinessLine = async (id: string, lineData: Partial<BusinessLine>) => {
    if (!currentCompany) {
      console.error('No current company found');
      return;
    }

    // Verificar si la empresa ha aceptado los términos
    if (!currentCompany.accept) {
      alert('Debe aceptar los términos y condiciones antes de modificar líneas de negocio. Vaya a "Políticas de Privacidad" en el menú para aceptar los términos.');
      return;
    }

    // Obtener límites desde la base de datos
    const maxWords = currentCompany.business_word || 10;
    
    if (lineData.keywords) {
      if (lineData.keywords.length > maxWords) {
        alert(`No se pueden agregar más de ${maxWords} palabras clave por línea de negocio.`);
        return;
      }
    }

    try {
      const { data, error } = await supabase
        .from('business_lines')
        .update(lineData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating business line:', error);
        
        if (error.message.includes('timeout') || error.message.includes('Request timeout')) {
          alert('Error de conexión: La operación tardó demasiado tiempo. Por favor, verifica tu conexión a internet e intenta nuevamente.');
        } else if (error.message.includes('JWT')) {
          alert('Error de autenticación: Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
        } else {
          alert(`Error al actualizar la línea de negocio: ${error.message}`);
        }
        return;
      }

      if (data) {
        setBusinessLines(prev => prev.map(line => 
          line.id === id ? data as BusinessLine : line
        ));
        console.log('Business line updated successfully:', data);
      }
    } catch (error) {
      console.error('Unexpected error updating business line:', error);
      alert('Error inesperado al actualizar la línea de negocio. Por favor, intenta nuevamente.');
    }
  };

  const deleteBusinessLine = async (id: string) => {
    try {
      const { error } = await supabase
        .from('business_lines')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting business line:', error);
        
        if (error.message.includes('timeout') || error.message.includes('Request timeout')) {
          alert('Error de conexión: La operación tardó demasiado tiempo. Por favor, verifica tu conexión a internet e intenta nuevamente.');
        } else if (error.message.includes('JWT')) {
          alert('Error de autenticación: Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
        } else {
          alert(`Error al eliminar la línea de negocio: ${error.message}`);
        }
        return;
      }

      setBusinessLines(prev => prev.filter(line => line.id !== id));
      console.log('Business line deleted successfully');
    } catch (error) {
      console.error('Unexpected error deleting business line:', error);
      alert('Error inesperado al eliminar la línea de negocio. Por favor, intenta nuevamente.');
    }
  };

  // Offers functions
  const addOffer = async (offer: Omit<Offer, 'id' | 'company_id' | 'created_at' | 'winner' | 'status'>): Promise<Offer | null> => {
    if (!currentCompany) {
      console.error('No current company available');
      alert('No hay una empresa seleccionada');
      return null;
    }

    try {
      // Asegurar que amount sea un número válido
      const amountValue = typeof offer.amount === 'number' ? offer.amount : parseFloat(String(offer.amount)) || 0;

      const offerData = {
        application_id: offer.application_id,
        company_id: currentCompany.id,
        provider_rut: offer.provider_rut.trim(),
        provider_name: offer.provider_name.trim(),
        currency: offer.currency,
        amount: amountValue,
        winner: false, // Por defecto, las nuevas ofertas no son ganadoras
        status: null // Por defecto, las nuevas ofertas no tienen estado
      };

      console.log('Inserting offer:', offerData);

      const { data, error } = await supabase
        .from('offers')
        .insert(offerData)
        .select()
        .single();

      if (error) {
        console.error('Error adding offer:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Error details:', error.details);
        console.error('Error hint:', error.hint);
        
        // Mensaje más descriptivo según el tipo de error
        let errorMessage = 'Error al agregar la oferta';
        if (error.code === '42P01') {
          errorMessage = 'La tabla de ofertas no existe. Por favor, ejecuta la migración de base de datos.';
        } else if (error.code === '42501') {
          errorMessage = 'No tienes permisos para agregar ofertas. Verifica las políticas RLS.';
        } else if (error.message) {
          errorMessage = `Error: ${error.message}`;
        }
        
        alert(errorMessage);
        return null;
      }

      if (!data) {
        console.error('No data returned from insert');
        alert('No se recibió respuesta del servidor al agregar la oferta');
        return null;
      }

      setOffers(prev => [data, ...prev]);
      console.log('Offer added successfully:', data);
      return data;
    } catch (error: any) {
      console.error('Unexpected error adding offer:', error);
      alert(`Error inesperado al agregar la oferta: ${error?.message || 'Error desconocido'}`);
      return null;
    }
  };

  const updateOffer = async (id: string, offer: Partial<Offer>): Promise<Offer | null> => {
    try {
      const { data, error } = await supabase
        .from('offers')
        .update(offer)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating offer:', error);
        return null;
      }

      setOffers(prev => prev.map(o => o.id === id ? data : o));
      return data;
    } catch (error) {
      console.error('Unexpected error updating offer:', error);
      return null;
    }
  };

  const deleteOffer = async (id: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('offers')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting offer:', error);
        return;
      }

      setOffers(prev => prev.filter(o => o.id !== id));
    } catch (error) {
      console.error('Unexpected error deleting offer:', error);
    }
  };

  const getOffersByApplication = (applicationId: string): Offer[] => {
    return offers.filter(o => o.application_id === applicationId);
  };

  return (
    <DataContext.Provider value={{
      organizations,
      organizationTypes,
      addOrganization,
      updateOrganization,
      deleteOrganization,
      tenderTypes,
      addTenderType,
      updateTenderType,
      deleteTenderType,
      tenderStatuses,
      addTenderStatus,
      updateTenderStatus,
      deleteTenderStatus,
      applicationStatuses,
      addApplicationStatus,
      updateApplicationStatus,
      deleteApplicationStatus,
      tenders,
      addTender,
      updateTender,
      deleteTender,
      tenderDocuments,
      addTenderDocument,
      updateTenderDocument,
      deleteTenderDocument,
      applications,
      addApplication,
      updateApplication,
      deleteApplication,
      products,
      addProduct,
      updateProduct,
      deleteProduct,
      productCategories,
      addProductCategory,
      updateProductCategory,
      deleteProductCategory,
      currencyRates,
      addCurrencyRate,
      updateCurrencyRate,
      deleteCurrencyRate,
      currencies,
      loadCurrencies,
      addCurrency,
      updateCurrency,
      deleteCurrency,
      plans,
      loadPlans,
      addPlan,
      updatePlan,
      deletePlan,
      businessLines,
      addBusinessLine,
      updateBusinessLine,
      deleteBusinessLine,
      offers,
      addOffer,
      updateOffer,
      deleteOffer,
      getOffersByApplication,
    }}>
      {children}
    </DataContext.Provider>
  );
};